import { memo, type ReactNode } from "react"
import { Icon } from "@/components/Icon"
import type { AppealActAmounts, AppealAmountBasis } from "../types"
import { HEAD_LABEL, deltaOf, formatDelta, formatRupees, gridTotal, headTotal, statedHeads } from "../lib/amounts"
import { amountChangeView, type PolarityView, type Tone } from "../lib/polarity"
import type { OiaNotice } from "../store/oia-case.store"

/** A pill that always carries words; colour is secondary (polarity.ts). */
export function TonePill({ tone, children, title }: { tone: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={`pill pill--${tone}`} title={title}>
      {children}
    </span>
  )
}

/** Label + the party it favours, e.g. "Appeal allowed · favours the taxpayer (appellant)". */
export function PolarityLine({ view, compact = false }: { view: PolarityView; compact?: boolean }) {
  return (
    <span className="oia-row" style={{ gap: 6 }}>
      <TonePill tone={view.tone}>{view.label}</TonePill>
      {!compact && <span className={`oia-tone--${view.tone}`} style={{ fontSize: 12 }}>{view.partyText}</span>}
    </span>
  )
}

export function NoticeBanner({ notice, onClose }: { notice: OiaNotice; onClose?: () => void }) {
  return (
    <div className={`oia-banner oia-banner--${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>
      <Icon name={notice.tone === "info" ? "sparkle" : "alert"} size={15} />
      <span className="oia-banner__text">{notice.text}</span>
      {onClose && (
        <button className="btn btn--quiet btn--sm" onClick={onClose} aria-label="Dismiss notice">
          <Icon name="x" size={13} />
        </button>
      )}
    </div>
  )
}

/**
 * Before / after amounts by head, with the change coloured by its effect on
 * the taxpayer under the grid's basis (a lower figure is relief on a demand,
 * a reduction of refund on a sanctioned refund). The figures are the
 * backend's; this only lays them out and adds the rows up.
 */
export const AmountsBeforeAfter = memo(function AmountsBeforeAfter({
  before,
  after,
  basis,
  beforeLabel = "As drafted",
  afterLabel = "After the change",
}: {
  before: AppealActAmounts | null
  after: AppealActAmounts | null
  basis: AppealAmountBasis
  beforeLabel?: string
  afterLabel?: string
}) {
  const heads = statedHeads(before, after)
  if (heads.length === 0) return null
  const totalBefore = gridTotal(before)
  const totalAfter = gridTotal(after)
  const change = amountChangeView(after === null ? null : deltaOf(totalBefore, totalAfter), basis)
  return (
    <div>
      <table className="oia-amounts">
        <thead>
          <tr>
            <th>Head</th>
            <th>{beforeLabel}</th>
            <th>{after === null ? "" : afterLabel}</th>
            <th>{after === null ? "" : "Change"}</th>
          </tr>
        </thead>
        <tbody>
          {heads.map((head) => {
            const b = headTotal(before, head)
            const a = after === null ? null : headTotal(after, head)
            const view = amountChangeView(after === null ? null : deltaOf(b, a), basis)
            return (
              <tr key={head}>
                <td>{HEAD_LABEL[head]}</td>
                <td>{formatRupees(b)}</td>
                <td>{after === null ? "" : formatRupees(a)}</td>
                <td className={`oia-tone--${view.tone}`}>{after === null ? "" : formatDelta(b, a)}</td>
              </tr>
            )
          })}
          <tr className="oia-amounts__total">
            <td>Total</td>
            <td>{formatRupees(totalBefore)}</td>
            <td>{after === null ? "" : formatRupees(totalAfter)}</td>
            <td className={`oia-tone--${change.tone}`}>{after === null ? "" : formatDelta(totalBefore, totalAfter)}</td>
          </tr>
        </tbody>
      </table>
      {after !== null && change.tone !== "neutral" && (
        <div className={`oia-tone--${change.tone}`} style={{ fontSize: 12, marginTop: 5, fontWeight: 600 }}>
          {change.label} — {change.partyText}
        </div>
      )}
      {after === null && (
        <div className="oia-muted" style={{ fontSize: 12, marginTop: 5 }}>
          The figure after the change is settled by the revise.
        </div>
      )}
    </div>
  )
})

export function Spinner({ size = 13 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size }} />
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}
